// §14 / §16 — settings that follow a signed-in user between devices (D-291).
//
// §14 says settings are "persisted to `settings` (server) or OPFS (guest)", and §16 has carried
// `GET/PUT /api/settings` — whole-object, last-writer-wins — since M12. The server half was
// already built and correct; nothing on the client had ever called it (D-290 found the same for
// the OPFS half, which did not exist at all).
//
// This is not "server *instead of* local". It is server **over** local, because §18 F1 is binding:
// a signed-in user who goes offline keeps working against OPFS. So every save is written locally
// first and pushed second, and a load that cannot reach the server answers from the local copy
// rather than resetting the desktop to defaults — which is what a strictly-remote backend would
// do the first time a laptop woke up on a bad network.
import type { SettingsBackend, SettingsValues } from './index';

export interface ServerSettingsOptions {
  /** The device-local store (§7.2). Always written, so an offline session keeps its settings. */
  local: SettingsBackend;
  /** Whether there is a signed-in user. A guest never touches the network (§8.3). */
  signedIn: () => boolean;
  /** Injected so tests need no network and no browser. */
  fetch?: typeof globalThis.fetch;
  /** Reported rather than thrown: losing a *sync* must not look like losing the setting. */
  onError?: (error: unknown) => void;
}

const ENDPOINT = '/api/settings';

/**
 * Is this a settings object we can use?
 *
 * A signed-in user with no row yet answers `{}`, which is a legitimate empty — distinguishing it
 * from "the request failed" is what lets first sign-in seed the server from the device instead of
 * wiping the device with a blank server.
 */
function objectOrNull(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

export function createServerSettingsBackend(options: ServerSettingsOptions): SettingsBackend {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  const report = options.onError ?? ((error: unknown) => console.warn('settings sync', error));

  const pull = async (): Promise<Record<string, unknown> | null> => {
    const response = await doFetch(ENDPOINT, { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`${ENDPOINT} answered ${response.status}`);
    const body = objectOrNull(await response.json());
    return body === null ? null : objectOrNull(body['settings']);
  };

  const push = async (values: SettingsValues): Promise<void> => {
    const response = await doFetch(ENDPOINT, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ settings: values }),
    });
    if (!response.ok) throw new Error(`${ENDPOINT} answered ${response.status}`);
  };

  return {
    async load() {
      const local = await options.local.load();
      if (!options.signedIn()) return local;

      let remote: Record<string, unknown> | null = null;
      try {
        remote = await pull();
      } catch (error) {
        // Offline, or the server is down. §18 F1: the desktop keeps working, which means it keeps
        // the settings it already had rather than snapping back to the defaults.
        report(error);
        return local;
      }
      if (remote === null) return local;

      // An account that has never saved anything — a first sign-in, or a first sign-in on any
      // device — inherits what this device was already set to, rather than blanking it. The push
      // happens on the next `save`, which `hydrate` triggers by writing the merged values back.
      if (Object.keys(remote).length === 0) return local;

      // Otherwise the server is the user's settings and it wins outright: §16 says last-writer-
      // wins, and a merge here would resurrect keys the user cleared on another device.
      await options.local.save(remote as SettingsValues);
      return remote;
    },

    async save(values) {
      // Local first and always. If the push fails the setting is still *set* — it is only the
      // other devices that have not heard yet.
      await options.local.save(values);
      if (!options.signedIn()) return;
      try {
        await push(values);
      } catch (error) {
        report(error);
      }
    },
  };
}
